// Client for POST /api/embed/scene-designer/price — the SceneDesigner posts
// its aggregated cart lines (resolved ProductIDs + quantities) and receives
// the PriceBreakdown that the price stat block, cart lines and PDF quote all
// render. 204 (no source / can't price) resolves to null and the price UI
// renders nothing — same contract as sceneFeatures.totalPrice always had.

export interface PriceLineRequest {
  productId: string;
  quantity: number;
  /** Optional measurements/config values (e.g. width_mm) for rule pricing. */
  parameters?: Record<string, string>;
}

export interface PriceRequestBody {
  items: PriceLineRequest[];
  currency?: string;
  market?: string;
  /** Scopes Designer-owned price rules server-side. */
  designerId?: string;
}

export interface BreakdownLineDto {
  productId: string;
  name: string;
  quantity: number;
  unitAmount: number;
  /** Units covered by an inclusion allowance — visible but not charged. */
  includedQuantity: number;
  lineTotal: number;
  priced: boolean;
  formattedUnitAmount: string | null;
  formattedLineTotal: string | null;
}

export interface PriceAdjustmentDto {
  label: string;
  amount: number;
  ruleId: string | null;
  formattedAmount: string | null;
}

export interface PriceBreakdownDto {
  lines: BreakdownLineDto[];
  adjustments: PriceAdjustmentDto[];
  subtotal: number;
  vatPercent: number | null;
  vatAmount: number;
  pricesIncludeVat: boolean;
  total: number;
  currency: string;
  /** False when some lines couldn't be priced — totals cover priced lines only. */
  complete: boolean;
  formattedSubtotal: string | null;
  formattedVatAmount: string | null;
  formattedTotal: string | null;
}

export interface FetchPriceBreakdownOptions {
  baseUrl?: string;
  body: PriceRequestBody;
  signal?: AbortSignal;
}

export async function fetchPriceBreakdown(
  opts: FetchPriceBreakdownOptions
): Promise<PriceBreakdownDto | null> {
  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const res = await fetch(`${base}/api/embed/scene-designer/price`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(opts.body),
    signal: opts.signal,
  });
  if (res.status === 204) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`fetchPriceBreakdown failed: ${res.status} ${res.statusText} ${text}`);
  }
  return (await res.json()) as PriceBreakdownDto;
}
